import { APIError, APIManagerResponse, APIRequestObj } from 'apiManager/types';
import { accountManager } from 'utils/accountManager';
import { gretch, GretchOptions } from 'gretchen';
import { getGretchOptions } from 'config/gretchen';
import { api } from 'api';
import { ApiErrorsCodeEnum } from 'config/apiErrorCodes';
import { translate } from 'translations';
import { parsePagination } from './utils';
import { OAUTH_SIGNAL_SERVER_URL } from 'config/api';

const getFetchOptions = (APIObj: APIRequestObj) => {
  const { requestConfig, additionalOpts } = APIObj;
  const options: GretchOptions = { ...getGretchOptions(requestConfig), ...additionalOpts };
  return options;
};

/**
 * Attempts to refresh the access token via OAuth TeamSystem ID Signal Server.
 * Returns the new access token or undefined if not applicable / failed.
 */
const refreshViaTsid = async (): Promise<string | undefined> => {
  const tsidSubValue = accountManager.cookie.tsidSub.get();
  const sessionId = accountManager.cookie.oauthSessionId.get();

  if (!tsidSubValue || !sessionId) return undefined;

  try {
    const response = await fetch(`${OAUTH_SIGNAL_SERVER_URL}/api/oauth/frontend/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tsidSub: tsidSubValue, sessionId })
    });

    if (response.ok) {
      const data: { accessToken: string; expiresIn: number } = await response.json();
      return data.accessToken;
    }
  } catch {
    // Signal server unreachable — fall through to legacy refresh
  }

  return undefined;
};

export const APIManager = async <T>(APIObj: APIRequestObj): Promise<APIManagerResponse<T>> => {
  const options = getFetchOptions(APIObj);
  const { response, status } = await gretch<T, APIError>(APIObj.url, options).flush();
  try {
    const { alreadyTriedToRefresh, justResponse } = APIObj.requestConfig;
    if (response.ok) {
      const pagination = parsePagination(response);
      // T must be to Response type if the option is enabled
      const data: T = justResponse ? response : await response.json();
      return { data, pagination };
    }
    const error = (await response.json()) as APIError;

    // try to get new access token for API with authorization
    // Check if we should attempt refresh: either 401 status or UNAUTHENTICATED error code
    if (
      !alreadyTriedToRefresh &&
      (status === 401 || ('code' in error && error.code === ApiErrorsCodeEnum.UNAUTHENTICATED))
    ) {
      // Try TeamSystem ID refresh first (if OAuth session exists)
      const tsidToken = await refreshViaTsid();
      if (tsidToken) {
        accountManager.updateAccessTokenInWholeApplication(tsidToken);
        const newAPIObj = { ...APIObj };
        newAPIObj.requestConfig.headerConfig = {
          ...newAPIObj.requestConfig.headerConfig,
          accessToken: tsidToken
        };
        newAPIObj.requestConfig.alreadyTriedToRefresh = true;
        return APIManager<T>(newAPIObj);
      }

      // Fallback to legacy refresh token
      const refreshToken = accountManager.cookie.refreshToken.get();
      if (refreshToken) {
        const newAccessToken = await api.authorization.getNewAccessTokenByRefreshToken(refreshToken);
        if (newAccessToken) {
          accountManager.updateAccessTokenInWholeApplication(newAccessToken);
          // update APIObj with new accessToken
          const newAPIObj = { ...APIObj };
          newAPIObj.requestConfig.headerConfig = {
            ...newAPIObj.requestConfig.headerConfig,
            accessToken: newAccessToken
          };
          newAPIObj.requestConfig.alreadyTriedToRefresh = true;
          return APIManager<T>(newAPIObj);
        }
      }
      return {
        error: { code: ApiErrorsCodeEnum.UNAUTHENTICATED, message: translate('loginSessionExpired'), status }
      };
    }

    if ('code' in error) {
      return { error: { ...error, status } };
    }

    throw Error;
  } catch (e) {
    return { error: { code: ApiErrorsCodeEnum.UNHANDLED_ERROR, message: translate('error_999'), status } };
  }
};
